import { getCookie } from '@utils/cookie';
import { getRoledAtAccessToken, getUserIdAtAccessToken } from '@utils/jwt';
import { createContext, useContext, useEffect, useState } from 'react';
import { useError } from '@hook/useError';
import { CLIENT_API } from '@api/services';
import { IAuthConextType, IIam, TAuthType } from './types';
import { removeCookie, setCookie } from '@utils/cookie';
import { PATHS } from '@routes/paths';
import {
  ACCESS_TOKEN_NAME,
  DEV_MODE_COOKIE_NAME,
  FRONT_URL,
  GOOGLE_OIDC_AUTH_URL,
  GOOGLE_OIDC_CLIENT_ID,
  NAVER_AUTH_URL,
  NAVER_CLIENT_ID,
  REFRESH_TOKEN_NAME,
} from '@utils/const';
import qs from 'qs';
import { axiosInstance } from '@utils/axios';

const AuthContext = createContext<IAuthConextType | undefined>(undefined);

export function AuthContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { handleError } = useError();

  const [iam, setIam] = useState<IIam | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = (authType: TAuthType) => {
    if (authType === 'GOOGLE') {
      return googleLogin();
    } else if (authType === 'KAKAO') {
      return kakaoLogin();
    } else if (authType === 'NAVER') {
      return naverLogin();
    }
  };

  const googleLogin = () => {
    const queryStr = qs.stringify({
      client_id: `${GOOGLE_OIDC_CLIENT_ID}`,
      redirect_uri: `${FRONT_URL}${PATHS.oAuth.google}`,
      response_type: 'code',
      scope: 'openid profile email',
      prompt: 'select_account',
    });
    const loginUrl = `${GOOGLE_OIDC_AUTH_URL}?${queryStr}`;
    window.location.href = loginUrl;
  };

  const kakaoLogin = () => {
    window.Kakao.Auth.authorize({
      redirectUri: `${FRONT_URL}${PATHS.oAuth.kakao}`,
    });
  };

  const naverLogin = () => {
    const naverReprompt = false;
    const queryStr = qs.stringify({
      client_id: `${NAVER_CLIENT_ID}`,
      redirect_uri: `${FRONT_URL}${PATHS.oAuth.naver}`,
      response_type: 'code',
      auth_type: `${naverReprompt ? 'reprompt' : 'reauthenticate'}`,
    });
    const loginUrl = `${NAVER_AUTH_URL}?${queryStr}`;

    window.location.href = loginUrl;
  };

  const signWithOAuth = async (authType: TAuthType, code: string) => {
    try {
      let accessToken;

      const {
        data: { renewAccessToken, renewRefreshToken: refreshToken },
      } = await CLIENT_API.UserApi.signOAuth({
        authType: authType,
        code,
        redirectUri: `${FRONT_URL}${PATHS.oAuth[authType.toLowerCase()]}`,
        joinedFrom: 'CCOB',
      });

      accessToken = renewAccessToken;

      if (!accessToken || !refreshToken) return;

      const hasAuth = getRoledAtAccessToken(accessToken).includes('PARTNER');

      if (!hasAuth) {
        const {
          data: { id },
        } = await axiosInstance({
          method: 'post',
          baseURL: process.env.NEXT_PUBLIC_SERVER_API_URL,
          url: '/v1/user-actions/partner/grant',
          headers: {
            accessToken,
          },
        });

        if (id) {
          const {
            data: { accessToken: reissuedAccessToken },
          } = await axiosInstance({
            method: 'post',
            baseURL: process.env.NEXT_PUBLIC_SERVER_API_URL,
            url: '/v1/user-actions/token/renew',
            headers: {
              refreshToken,
            },
          });
          accessToken = reissuedAccessToken;
        }
      }

      const isLocalhost = process.env.NODE_ENV === 'development';
      const domain = isLocalhost ? 'localhost' : '.cccv.to';
      setCookie(ACCESS_TOKEN_NAME, accessToken, { path: '/', domain });
      setCookie(REFRESH_TOKEN_NAME, refreshToken, { path: '/', domain });
      window.location.replace(PATHS.main);
    } catch (e) {
      handleError(e);
      window.location.replace(PATHS.page500);
    }
  };

  const logout = () => {
    const isLocalhost = process.env.NODE_ENV === 'development';
    const domain = isLocalhost ? 'localhost' : '.cccv.to';
    removeCookie(ACCESS_TOKEN_NAME, { path: '/', domain });
    removeCookie(ACCESS_TOKEN_NAME, { path: '/', domain: 'dev-ccob.cccv.to' });
    removeCookie(REFRESH_TOKEN_NAME, { path: '/', domain });
    removeCookie(DEV_MODE_COOKIE_NAME, { path: '/' });
    window.location.replace(PATHS.login);
  };

  //TODO: api 신규 작성되면 수정
  // const validateJwtToken = async (token, tokenType) => {
  //   try {
  //     await CLIENT_API.TokenApi.validateJwtToken({
  //       token,
  //       tokenType,
  //     });
  //   } catch (e) {
  //     handleError(e);
  //     setIsLoading(false);
  //   }
  // };

  const getUser = async (userId): Promise<IIam | undefined> => {
    try {
      const { data: userInfo } = await CLIENT_API.UserApi.getUser(userId);
      return userInfo;
    } catch (e) {
      handleError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const initialize = async () => {
    const accessToken = getCookie(ACCESS_TOKEN_NAME);
    const refreshToken = getCookie(REFRESH_TOKEN_NAME);
    if (!accessToken || !refreshToken) {
      setIsLoading(false);
      return console.error('사용자 Auth Token 정보를 찾을 수 없습니다.');
    }

    /**
     * 토큰 유효성 검증
     */
    // await validateJwtToken(accessToken, 'ACCESS');

    /**
     * 유저 기본 정보 호출 및 State에 저장
     */

    const userId = getUserIdAtAccessToken(accessToken);
    const userInfo = await getUser(userId);
    if (userInfo) {
      setIam(userInfo);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  if (isLoading) {
    return <></>;
  }

  return (
    <AuthContext.Provider
      value={{
        iam,
        logout,
        login,
        signWithOAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within a AuthContextProvier');
  }
  return context;
};
